/**
 * Orchestrator runner — CHUNK 1 ONLY.
 *
 * This is intentionally a minimal first-cut. We are NOT yet building the full
 * dispatch system — that comes in later chunks. The job of this chunk is:
 *
 *   1. Build the queen's first user message programmatically by composing a
 *      fixed meta-prompt template with two substitutions:
 *        - the team list (one entry per role card, with the resolved file
 *          path of the role's `.md` definition on disk)
 *        - the user's actual task prompt
 *      This is the user's "very simple parser" pattern — the queen never
 *      sees the raw user prompt at the top level; she only ever receives
 *      the wrapped meta-prompt.
 *
 *   2. Spawn a single queen agent (full Appostle session, internal flag set
 *      so it does not appear in the sidebar by default — the user can
 *      Promote it from the quest panel to inspect what she's doing).
 *
 *   3. Send the meta-prompt as the queen's first user message and wait for
 *      her run to complete.
 *
 *   4. When she returns, mark the quest as succeeded. There is no plan
 *      parsing, no worker dispatch, no hivemind doc enforcement at this
 *      stage — the queen has tools and can write the hivemind doc herself
 *      if her response chooses to. Whatever she does, we observe.
 *
 * Tool access for the queen in this chunk:
 *   - Read, Glob, Grep, Write (so she can read every role file by path and
 *     start the hivemind doc as the meta-prompt instructs).
 *   - NO Bash, NO WebFetch, NO Edit, NO Task/Agent (built-in subagent),
 *     NO NotebookEdit. We don't want her to start doing work or spawn
 *     ephemeral subagents — the meta-prompt explicitly forbids both.
 *
 * Lifecycle: the queen agent is NOT closed by the runner — staying alive
 * lets the user Promote-to-session at any time. AgentManager owns the
 * lifecycle (same convention as we settled on previously).
 */

import { access, readFile } from "node:fs/promises";
import path from "node:path";
import { fileURLToPath } from "node:url";
import type { Logger } from "pino";
import type { AgentSessionConfig, McpServerConfig } from "../agent/agent-sdk-types.js";
import { getProviderFullAccessModeId } from "../agent/provider-manifest.js";
import { listRoles, resolveRole, type RoleEntry } from "../roles/scanner.js";
import { buildOrchestratorMcpServer } from "./orchestrator-mcp.js";
import type { QuestRunner, QuestService } from "./service.js";
import { QuestCardSchema, type QuestRecord } from "./types.js";
import { buildAgentOwnerOptions } from "./agent-owner-options.js";

// ---------------------------------------------------------------------------
// Tool whitelist for the queen in chunk 2
//
// The queen has Read/Glob/Grep so she can study the role files; Write so she
// can author the hivemind doc; and the in-process `appostle` MCP server's
// `handoff` tool so she can spawn worker sessions. She does NOT have Bash,
// WebFetch, Edit, or any built-in subagent tool — by design, so she cannot
// shell out, edit configs, or invoke ephemeral subagents.
// ---------------------------------------------------------------------------

const QUEEN_ALLOWED_TOOLS: string[] = ["Read", "Glob", "Grep", "Write", "mcp__appostle__handoff"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function nowIso(): string {
  return new Date().toISOString();
}

interface TeamMember {
  /** 1-based card index this member corresponds to in `record.cards`. */
  cardIndex: number;
  /** Role name as the user typed it on the card. May be null if the user
   *  staged a card with no role assigned — we still emit it so the queen
   *  can see the gap. */
  name: string | null;
  /** Absolute filesystem path to the role's `.md` file, or null when the
   *  role name didn't resolve against the scanner. */
  path: string | null;
  /** First-pass description from the role frontmatter (so the queen has
   *  some context even before she reads the full body). */
  description: string;
}

async function resolveTeam(record: QuestRecord): Promise<TeamMember[]> {
  // Build the directory of all roles once (project + global). Project
  // shadows global — listRoles handles that ordering.
  let roleIndex: RoleEntry[] = [];
  try {
    roleIndex = await listRoles({ workspaceRoot: record.cwd });
  } catch {
    // Best-effort. If listing fails the queen will see all paths as null and
    // can decide what to do.
  }
  return record.cards.map((card) => {
    if (!card.role) {
      return { cardIndex: card.index, name: null, path: null, description: "" };
    }
    const match = roleIndex.find((r) => r.name === card.role);
    return {
      cardIndex: card.index,
      name: card.role,
      path: match?.path ?? null,
      description: match?.description ?? "",
    };
  });
}

/**
 * Embedded fallback prompt. Used only when `queen-prompt.md` cannot be found
 * on disk (e.g. running outside a dev checkout). Kept in sync verbatim with
 * the on-disk version. Edit the on-disk file for fast iteration; only update
 * this fallback when you also commit the new template alongside the daemon.
 */
const FALLBACK_QUEEN_PROMPT_TEMPLATE = [
  "you are going to send out agents, but first you get aquanted with 'the team'",
  "You don't go do the work yourself!",
  "",
  "this team has very deep knowledge of the task at hand. they have very detailed skills and output prefs so they are smarter than you. don't assume otherwise.",
  "",
  "the team is split up in categories. you will try to consolidate all of their knowledge into an approach for the task below.",
  "",
  "",
  "The team:",
  "",
  "{{team}}",
  "",
  "===",
  "",
  "The task at hand:",
  "",
  "",
  ">>>>>",
  "{{task}}",
  "<<<<<<<<<<",
  "",
  "Again, whatever you are thinking of now I repeat: You don't go do the work yourself!",
  "What your job IS tho:",
  "",
  "Now you have full knowledge of what the user wants and how he composed his 'team'",
  "you do not delegate this team as subagents but you spawn new handoff sessions, you decide on what can be done in paralel and what needs to happen before the next can start. stage. the amount of handof sessions you spawn is equal to the amount of team entries in your prompt.",
  "",
  "",
  "",
  "We are going to use a hivemind dock in the repo.",
  "What you can do, is now startup that hivemind doc in /.hivemind/orchestrator-{{questId}}.md",
  "",
  "",
  "You instruct every handoff with a clear instruction on it's task.",
  "But you also make sure that each handoff knows the greater context of the end goal and knows of the existence of the hivemind document.",
  "it should know that it can write to it but first needs to check if there are no conflicts.",
  "",
  "",
  "do not use subagents because these are emphetic. we need full sessions.",
].join("\n");

const QUEEN_PROMPT_FILENAME = "queen-prompt.md";
const QUEEN_PROMPT_MAX_LOOKUP_LEVELS = 10;

/**
 * Locate `queen-prompt.md` on disk. Walks up from the runner file's directory
 * up to the filesystem root, returning the first hit. Returns `null` when the
 * file cannot be found anywhere on the path — the caller falls back to the
 * embedded template.
 *
 * Why this approach: in dev the file lives at the repo root; in a built
 * `dist/` tree it can also be co-located next to the bundled runner. Walking
 * up handles both layouts without hardcoding paths.
 */
async function findQueenPromptOnDisk(): Promise<string | null> {
  const start = path.dirname(fileURLToPath(import.meta.url));
  let cursor = start;
  for (let i = 0; i < QUEEN_PROMPT_MAX_LOOKUP_LEVELS; i += 1) {
    const candidate = path.join(cursor, QUEEN_PROMPT_FILENAME);
    try {
      await access(candidate);
      return candidate;
    } catch {
      // not at this level — keep walking up
    }
    const parent = path.dirname(cursor);
    if (parent === cursor) break;
    cursor = parent;
  }
  return null;
}

/**
 * Read the queen prompt template fresh on every quest start so a developer
 * can edit `queen-prompt.md` and rerun a quest without restarting the daemon.
 * Falls back to the embedded constant when the file is missing or unreadable.
 */
async function loadQueenPromptTemplate(logger: Logger): Promise<{
  template: string;
  source: string;
}> {
  const onDisk = await findQueenPromptOnDisk();
  if (onDisk) {
    try {
      const template = await readFile(onDisk, "utf8");
      return { template, source: onDisk };
    } catch (err) {
      logger.warn(
        { err, path: onDisk },
        "orchestrator: queen prompt found on disk but could not be read; using embedded fallback",
      );
    }
  } else {
    logger.warn("orchestrator: queen prompt not found on disk; using embedded fallback");
  }
  return { template: FALLBACK_QUEEN_PROMPT_TEMPLATE, source: "<embedded fallback>" };
}

function renderTeamLines(team: TeamMember[]): string {
  return team
    .map((m, i) => {
      const idx = i + 1;
      if (!m.name) return `[value ${idx}, (no role assigned to this card)]`;
      const filePath = m.path ?? "(role file not resolved on disk)";
      const desc = m.description ? ` — ${m.description}` : "";
      return `[value ${idx}, ${m.name}${desc}, ${filePath}]`;
    })
    .join("\n\n");
}

/**
 * Substitutes `{{team}}`, `{{task}}`, and `{{questId}}` into the template.
 * Uses split/join (not regex) so any literal braces in the user's task
 * prompt do not get re-interpreted as placeholder syntax.
 */
function buildMetaPrompt(args: {
  template: string;
  team: TeamMember[];
  userPrompt: string;
  questId: string;
}): string {
  return args.template
    .split("{{team}}")
    .join(renderTeamLines(args.team))
    .split("{{task}}")
    .join(args.userPrompt)
    .split("{{questId}}")
    .join(args.questId);
}

/**
 * Resolves a role file on disk to a full AgentSessionConfig the daemon can
 * spawn. Used by the orchestrator MCP server's `handoff` tool — the runner
 * owns this translation because role parsing semantics (frontmatter +
 * allowed-tools + model) live in the roles module, not in the MCP server.
 *
 * Strategy: derive the role NAME from the file path (filename without `.md`),
 * resolve via the roles scanner so we reuse its frontmatter parsing exactly.
 * Falls back to a vanilla config (just cwd + provider) when the role can't
 * be resolved — the worker will still spawn and at least report the issue.
 */
async function resolveWorkerConfigForHandoff(args: {
  record: QuestRecord;
  rolePath: string;
  taskTitle: string;
}): Promise<AgentSessionConfig> {
  const { record, rolePath, taskTitle } = args;
  const modeId = getProviderFullAccessModeId(record.defaultProvider) ?? undefined;
  const baseConfig: AgentSessionConfig = {
    provider: record.defaultProvider,
    cwd: record.cwd,
    model: record.defaultModel ?? undefined,
    modeId,
    title: `quest ${record.id} handoff: ${taskTitle}`,
    internal: true,
  };

  // Derive role name from path (strip dirname + extension). The scanner
  // matches on the `name` frontmatter field, which by convention equals the
  // file stem. If the role file uses a different `name:` value internally,
  // we'll fall through to the path-only worker spawn.
  const roleName = path.basename(rolePath, path.extname(rolePath));
  try {
    const resolved = await resolveRole({
      roleName,
      workspaceRoot: record.cwd,
    });
    if (resolved) {
      const config: AgentSessionConfig = { ...baseConfig };
      if (resolved.body) config.systemPrompt = resolved.body;
      if (resolved.allowedTools.length > 0) config.allowedTools = resolved.allowedTools;
      return config;
    }
  } catch {
    // fall through to base config
  }
  return baseConfig;
}

function buildQueenAgentConfig(args: {
  record: QuestRecord;
  mcpServer: McpServerConfig;
}): AgentSessionConfig {
  const modeId = getProviderFullAccessModeId(args.record.defaultProvider) ?? undefined;
  return {
    provider: args.record.defaultProvider,
    cwd: args.record.cwd,
    // Queen runs on whatever model the user picked at the quest level. We
    // don't hard-code a model here — observe behaviour across model choices.
    model: args.record.defaultModel ?? undefined,
    modeId,
    // No system prompt overlay. The queen's instruction is the first user
    // message (the meta-prompt). She gets a vanilla agent identity from the
    // provider preset.
    allowedTools: QUEEN_ALLOWED_TOOLS,
    // In-process MCP server with the `handoff` tool. Registered under the
    // name `appostle`, so the queen sees `mcp__appostle__handoff`.
    mcpServers: { appostle: args.mcpServer },
    title: `quest ${args.record.id} queen`,
    internal: true,
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

export interface OrchestratorRunnerOptions {
  logger: Logger;
}

export class OrchestratorRunner implements QuestRunner {
  private readonly logger: Logger;

  constructor(options: OrchestratorRunnerOptions) {
    this.logger = options.logger.child({ module: "quest-runner-orchestrator" });
  }

  async execute(args: {
    questId: string;
    signal: AbortSignal;
    service: QuestService;
  }): Promise<void> {
    const { questId, signal, service } = args;
    const initial = service.getRecord(questId);
    if (!initial) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (initial.kind !== "orchestrate") {
      throw new Error(`OrchestratorRunner cannot execute quest of kind "${initial.kind}"`);
    }
    if (initial.termination.type !== "orchestrate") {
      throw new Error('OrchestratorRunner expects termination.type === "orchestrate"');
    }
    if (initial.cards.length === 0) {
      await service.markStatus(questId, "failed");
      throw new Error("Orchestrate quest requires at least one role card");
    }

    // ---------------------------------------------------------------------
    // Build the queen's first user message (the meta-prompt).
    // Read the template fresh on every quest start so a developer can edit
    // `queen-prompt.md` and rerun without restarting the daemon.
    // ---------------------------------------------------------------------
    const team = await resolveTeam(initial);
    const { template, source: templateSource } = await loadQueenPromptTemplate(this.logger);
    this.logger.info({ questId, templateSource }, "orchestrator: queen prompt loaded");
    const metaPrompt = buildMetaPrompt({
      template,
      team,
      userPrompt: initial.prompt,
      questId: initial.id,
    });

    // ---------------------------------------------------------------------
    // Build the in-process MCP server the queen will use to spawn handoffs.
    // The runner owns role-file → AgentSessionConfig translation; the MCP
    // server stays purely transport.
    // ---------------------------------------------------------------------
    const cardIndexByRolePath = new Map<string, number>();
    for (const member of team) {
      if (member.path) cardIndexByRolePath.set(member.path, member.cardIndex);
    }

    const mcpServer = buildOrchestratorMcpServer({
      questId: initial.id,
      agentManager: service.agentManager,
      logger: this.logger,
      signal,
      // Forward the quest creator's identity so handoff-spawned workers are
      // scoped to the same user. Without this the queen's children leak into
      // every other user's sidebar on a shared host.
      workerOwnerOptions: buildAgentOwnerOptions(initial),
      resolveWorkerConfig: ({ rolePath, taskTitle }) =>
        resolveWorkerConfigForHandoff({
          record: initial,
          rolePath,
          taskTitle,
        }),
      onHandoffSpawned: ({ rolePath, workerAgentId }) => {
        const cardIndex = cardIndexByRolePath.get(rolePath);
        if (cardIndex === undefined) {
          this.logger.warn(
            { rolePath, questId: initial.id },
            "orchestrator: handoff for unknown rolePath — no card to attach",
          );
          return;
        }
        // Stamp the card with the worker's agentId + running status. Awaiting
        // is unnecessary here — the MCP handler runs in parallel with the
        // queen's continuation, and persistence is best-effort for the UI.
        void service
          .updateCard(initial.id, cardIndex, {
            status: "running",
            agentId: workerAgentId,
            startedAt: nowIso(),
          })
          .catch((err) =>
            this.logger.warn(
              { err, cardIndex },
              "orchestrator: failed to stamp card with worker agentId",
            ),
          );
      },
      onHandoffCompleted: ({ rolePath, status: workerStatus }) => {
        const cardIndex = cardIndexByRolePath.get(rolePath);
        if (cardIndex === undefined) return;
        void service
          .updateCard(initial.id, cardIndex, {
            status: workerStatus,
            completedAt: nowIso(),
          })
          .catch((err) =>
            this.logger.warn(
              { err, cardIndex, workerStatus },
              "orchestrator: failed to stamp card with worker completion",
            ),
          );
      },
    });

    // ---------------------------------------------------------------------
    // Spawn the queen.
    // ---------------------------------------------------------------------
    const queenStartedAt = nowIso();
    const queenAgent = await service.agentManager.createAgent(
      buildQueenAgentConfig({ record: initial, mcpServer }),
      undefined,
      buildAgentOwnerOptions(initial),
    );

    const queenCard = QuestCardSchema.parse({
      index: 0,
      role: "queen",
      model: initial.defaultModel,
      prompt: null,
      wiring: { inputs: "user-prompt", outputs: "next-card" },
      status: "running",
      agentId: queenAgent.id,
      startedAt: queenStartedAt,
      completedAt: null,
      verify: null,
    });
    await service.setQueenCard(questId, queenCard);

    const onAbort = () => {
      void service.agentManager.cancelAgentRun(queenAgent.id).catch(() => {});
    };
    signal.addEventListener("abort", onAbort);

    // ---------------------------------------------------------------------
    // Run the queen with the meta-prompt and wait.
    // ---------------------------------------------------------------------
    let finalStatus: QuestRecord["status"] = "succeeded";
    try {
      const queenRun = await service.agentManager.runAgent(queenAgent.id, metaPrompt);
      if (queenRun.canceled || signal.aborted) {
        await service.updateQueenCard(questId, {
          status: "stopped",
          completedAt: nowIso(),
        });
        await service.markStatus(questId, "stopped");
        return;
      }
      await service.updateQueenCard(questId, {
        status: "succeeded",
        completedAt: nowIso(),
      });
    } catch (err) {
      this.logger.error({ err, questId }, "orchestrator: queen errored");
      await service.updateQueenCard(questId, {
        status: "failed",
        completedAt: nowIso(),
      });
      finalStatus = "failed";
    } finally {
      signal.removeEventListener("abort", onAbort);
      // Intentionally do NOT close the queen agent here. The user can
      // Promote-to-session at any time to inspect her conversation,
      // including reasoning and any files she produced. AgentManager
      // owns the lifecycle.
    }

    // Reconcile worker cards now that the queen's turn is over.
    //   - "queued" → the queen never dispatched this role. Mark stopped.
    //   - "running" → the queen's tool call is still in-flight (shouldn't
    //     happen because the SDK awaits the tool result inside her turn,
    //     but if it does we mark it succeeded since runAgent returned).
    // The MCP `handoff` handler already stamps cards with the worker's final
    // status as it completes — this loop only catches stragglers.
    const current = service.getRecord(questId);
    if (current) {
      for (const card of current.cards) {
        if (card.status === "queued") {
          await service.updateCard(questId, card.index, {
            status: "stopped",
            completedAt: nowIso(),
          });
        }
      }
    }

    await service.markStatus(questId, finalStatus);
  }
}
